import React from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloseIcon from '@mui/icons-material/Close';
import HomeIcon from '@mui/icons-material/Home';
import LanguageIcon from '@mui/icons-material/Language';
import MenuIcon from '@mui/icons-material/Menu';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LoginDialogMulti from './LoginDialogMulti';
import ChangePasswordDialog from './ChangePasswordDialog';

const navItems = [
  { path: '/pathasalaMgmt', icon: PersonAddAlt1Icon, label: { telugu: 'పాఠశాల నిర్వహణ', sanskrit: 'पाठशाला व्यवस्थापनम्', english: 'Pathashala Management' } },
  { path: '/parikshaMgmt', icon: AssessmentIcon, label: { telugu: 'పరీక్ష విభాగం', sanskrit: 'परीक्षा विभागः', english: 'Exam Section' } },
  { path: '/admin', icon: AssessmentIcon, label: { telugu: 'అడ్మిన్ విభాగం', sanskrit: 'प्रशासन विभागः', english: 'Administration' }, roles: ['Admin', 'SuperUser'] },
  { path: '/member-management', icon: AssessmentIcon, label: { telugu: 'సభ్యుల నమోదు విభాగం', sanskrit: 'सदस्य पञ्जीकरण विभागः', english: 'Member Management' }, roles: ['Admin', 'Startup'] }
];

const dropdownItems = {
  pariksha: [
    { path: '/pariksha-syllabus', label: 'Pariksha Syllabus' },
    { path: '/vatuvu', label: 'Exam Applications' },
    { path: '/pariksha-results', label: 'Pariksha Results' },
    { path: '/pariksha-adhikaris', label: 'Pariksha Adhikaris' },
    { path: '/parishaadhikari', label: 'Examiner Section' },
    { path: '/pathasala-exam-status', label: 'Pathasala Exam Status', roles: ['PathashalaAdmin'] },
    { path: '/download-patta', label: 'Download Patta' }
  ],
  admin: [
    { path: '/admin/registration-report', label: 'Registration Report', roles: ['Admin'] },
    { path: '/admin/pass-report', label: 'Pass Report', roles: ['Admin'] },
    { path: '/admin/examiner-assignments', label: 'Examiner Assignments', roles: ['Admin'] },
    { path: '/admin/patta-generation', label: 'Patta Generation', roles: ['Admin'] },
    { path: '/admin/panasala-antyakshari', label: 'Panasala Antyakshari', roles: ['Admin'] },
    { path: '/admin/slideshow', label: 'Slideshow', roles: ['Admin'] },
    { path: '/admin/homepage-slideshow', label: 'Homepage Slideshow', roles: ['Admin'] }
  ],
  pathasala: [
    { path: '/pathasala-exam-status', label: 'Pathasala Exam Status', roles: ['PathashalaAdmin'] },
    { path: '/pariksha-results', label: 'Pariksha Results' },
    { path: '/pathasalaMgmt', label: 'Pathasala Management' }
  ]
};

function LanguageSelect() {
  const { lang, setLang } = useLanguage();
  return (
    <Select
      value={lang}
      onChange={(event) => setLang(event.target.value)}
      variant="standard"
      IconComponent={ArrowForwardIcon}
      inputProps={{ 'aria-label': 'Select language' }}
      startAdornment={<LanguageIcon sx={{ color: '#c88a2d', mr: 0.6, fontSize: 18 }} />}
      sx={{
        minWidth: { xs: 92, sm: 112 }, color: '#382b25', fontWeight: 700, fontSize: '0.78rem',
        '&:before, &:after': { display: 'none' }, '& .MuiSelect-select': { py: 0.8, pr: '24px !important' },
        '& .MuiSelect-icon': { transform: 'rotate(90deg)', color: '#c88a2d', right: 0 }
      }}
    >
      <MenuItem value="telugu">తెలుగు</MenuItem>
      <MenuItem value="sanskrit">संस्कृतम्</MenuItem>
      <MenuItem value="english">English</MenuItem>
    </Select>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { lang } = useLanguage();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [profileAnchor, setProfileAnchor] = React.useState(null);
  const [loginDialogOpen, setLoginDialogOpen] = React.useState(false);
  const [changePasswordDialogOpen, setChangePasswordDialogOpen] = React.useState(false);
  const [pathasalaConfirmOpen, setPathasalaConfirmOpen] = React.useState(false);
  const [dropdownAnchor, setDropdownAnchor] = React.useState(null);
  const [activeDropdown, setActiveDropdown] = React.useState(null);

  React.useEffect(() => {
    if (user?.requirePasswordChange) setChangePasswordDialogOpen(true);
  }, [user]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (location.pathname === '/' && params.get('openPathasala') === 'true') {
      setPathasalaConfirmOpen(true);
      navigate('/', { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  const visibleNavItems = navItems.filter(item => !item.roles || (user && item.roles.includes(user.role)));
  const copy = {
    telugu: { registerTitle: 'పాఠశాల నమోదు', register: 'నమోదు', exit: 'నిష్క్రమించు', message: <>మీరు పాఠశాల యొక్క పరిపాలకుడు/యజమాని అయితే, దయచేసి <strong>నమోదు</strong> పై క్లిక్ చేయండి; లేకపోతే <strong>నిష్క్రమించు</strong> క్లిక్ చేయండి.</> },
    sanskrit: { registerTitle: 'पाठशाला पञ्जीकरणम्', register: 'Proceed', exit: 'Exit', message: <>यदि भवन्तः पाठशालायाः प्रशासकः वा स्वामी सन्ति तर्हि <strong>Proceed</strong> इत्यत्र क्लिक्य पञ्जीकरणं कुर्वन्तु।</> },
    english: { registerTitle: 'Pathashala registration', register: 'Proceed', exit: 'Exit', message: <>If you are a pathashala administrator or owner, click <strong>Proceed</strong> to register it.</> }
  }[lang] || {};

  const closeDrawer = () => setDrawerOpen(false);
  const handleLogout = async () => {
    setProfileAnchor(null);
    await logout();
  };

  const visibleDropdownItems = key => dropdownItems[key].filter(item => !item.roles || (user && item.roles.includes(user.role)));
  const openDropdown = (event, key) => {
    setDropdownAnchor(event.currentTarget);
    setActiveDropdown(key);
  };
  const closeDropdown = () => {
    setDropdownAnchor(null);
    setActiveDropdown(null);
  };
  const goToDropdownItem = path => {
    closeDropdown();
    if (path === '/pathasalaMgmt') setPathasalaConfirmOpen(true);
    else navigate(path);
  };

  const drawerContent = (
    <Box sx={{ height: '100%', background: '#fffaf1', p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src="/assets/Vidyaranyam_Logo.jpeg" alt="Vedaaranyam logo" sx={{ width: 36, height: 36, borderRadius: '50%' }} />
          <Typography sx={{ fontWeight: 900, color: '#d9672b' }}>వేదారణ్యం</Typography>
        </Box>
        <IconButton onClick={closeDrawer} aria-label="Close navigation" sx={{ color: '#d9672b' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider sx={{ borderColor: 'rgba(200,138,45,0.2)' }} />
      <List sx={{ mt: 1 }}>
        {visibleNavItems.map(item => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return (
            <ListItemButton
              key={item.path}
              onClick={() => {
                if (item.path === '/pathasalaMgmt') setPathasalaConfirmOpen(true);
                else navigate(item.path);
                closeDrawer();
              }}
              sx={{ borderRadius: 2, mb: 0.5, color: active ? '#fff' : '#796a60', background: active ? 'linear-gradient(110deg, #d9672b, #c88a2d)' : 'transparent', '&:hover': { backgroundColor: active ? undefined : '#f8ead8' } }}
            >
              <ListItemIcon sx={{ minWidth: 38, color: 'inherit' }}><Icon /></ListItemIcon>
              <Typography sx={{ fontSize: '0.84rem', fontWeight: active ? 800 : 600 }}>{item.label[lang] || item.label.english}</Typography>
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(145deg, #fffdf8 0%, #fff5e8 50%, #f9e3cb 100%)' }}>
      <AppBar position="fixed" elevation={0} sx={{ paddingBottom: 0.5, background: 'rgba(86, 57, 38, 0.72)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', px: { xs: 2, md: 2 }, pt: { xs: 0.5, md: 0.5 }, zIndex: theme => theme.zIndex.drawer + 1 }}>
        <Toolbar
          sx={{
            maxWidth: 1320,
            width: '100%',
            mx: 'auto',
            minHeight: '72px !important',
            px: { xs: 0.5, sm: 1.5, md: 2 },
            py: 1,
            border: '1px solid rgba(255,255,255,0.52)',
            borderRadius: '26px',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.72), rgba(255,255,255,0.34)), rgba(255,248,232,0.16)',
            backdropFilter: 'blur(20px) saturate(135%)',
            WebkitBackdropFilter: 'blur(20px) saturate(135%)',
            boxShadow: '0 18px 42px rgba(105,64,28,0.12), inset 0 1px 0 rgba(255,255,255,0.7)',
            gap: { xs: 0.8, md: 1.2 }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, p: 0.45, borderRadius: 99, background: 'rgba(255,255,255,0.38)', border: '1px solid rgba(255,255,255,0.5)', boxShadow: 'inset 0 1px 4px rgba(111,73,37,0.06)' }}>
            <IconButton onClick={() => setDrawerOpen(open => !open)} aria-label={drawerOpen ? 'Close navigation' : 'Open navigation'} sx={{ width: 38, height: 38, color: '#fff', background: 'linear-gradient(145deg, #dc7a38, #b9662e)', boxShadow: '0 5px 12px rgba(185,102,46,0.25)', '&:hover': { background: '#ad5d29' } }}>
              <MenuIcon />
            </IconButton>
            <Box component={RouterLink} to="/" sx={{ display: 'flex', alignItems: 'center', gap: 0.8, textDecoration: 'none', pr: 1 }}>
              <Box component="img" src="/assets/Vidyaranyam_Logo.jpeg" alt="Vedaaranyam logo" sx={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '2px solid #edc276' }} />
              <Typography sx={{ display: { xs: 'none', sm: 'block' }, color: '#d9672b', fontWeight: 900, fontSize: '0.9rem', whiteSpace: 'nowrap' }}>వేదారణ్యం</Typography>
            </Box>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.4, p: 0.55, px: 0.8, borderRadius: 99, background: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.42)' }}>
            <IconButton onClick={() => navigate('/')} aria-label="Home" sx={{ width: 34, height: 34, color: location.pathname === '/' ? '#d9672b' : '#806b5d', background: location.pathname === '/' ? 'rgba(255,224,172,0.58)' : 'transparent' }}><HomeIcon sx={{ fontSize: 19 }} /></IconButton>
            {['pariksha', 'pathasala', 'admin'].map(key => {
              const isAdminMenu = key === 'admin';
              if (isAdminMenu && !user?.role || (isAdminMenu && !['Admin', 'SuperUser'].includes(user.role))) return null;
              return (
                <Button
                  key={key}
                  onClick={event => openDropdown(event, key)}
                  endIcon={<ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
                  sx={{ color: '#715b4d', fontWeight: 700, fontSize: '0.76rem', textTransform: 'none', borderRadius: 99, px: 1.1, '&:hover': { background: 'rgba(255,224,172,0.5)' } }}
                >
                  {key === 'pariksha' ? 'Pariksha' : key === 'pathasala' ? 'Pathasala Management' : 'Admin'}
                </Button>
              );
            })}
          </Box>

          <Menu
            anchorEl={dropdownAnchor}
            open={Boolean(dropdownAnchor)}
            onClose={closeDropdown}
            PaperProps={{ sx: { mt: 1, minWidth: 230, borderRadius: 2, background: 'rgba(255,250,241,0.97)', boxShadow: '0 14px 35px rgba(105,64,28,0.18)' } }}
          >
            {activeDropdown && visibleDropdownItems(activeDropdown).map(item => (
              <MenuItem key={item.path} onClick={() => goToDropdownItem(item.path)} sx={{ py: 1.1, color: '#715b4d', fontSize: '0.82rem', fontWeight: 600, '&:hover': { background: '#f8ead8' } }}>
                {item.label}
              </MenuItem>
            ))}
          </Menu>

          <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 0.45, p: 0.55, borderRadius: 99, background: 'rgba(255,255,255,0.26)', border: '1px solid rgba(255,255,255,0.4)' }}>
            {[AssessmentIcon, HomeIcon, PersonAddAlt1Icon].map((Icon, index) => <Box key={index} sx={{ width: 32, height: 32, display: 'grid', placeItems: 'center', borderRadius: '50%', color: index === 1 ? '#c88a2d' : '#937665', background: index === 1 ? 'rgba(239,195,103,0.25)' : 'rgba(255,255,255,0.28)' }}><Icon sx={{ fontSize: 17 }} /></Box>)}
          </Box>

          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 0.4, sm: 0.8 }, p: 0.45, pl: { xs: 0.7, sm: 1 }, borderRadius: 99, background: 'rgba(255,255,255,0.38)', border: '1px solid rgba(255,255,255,0.5)' }}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.6, px: 0.9, color: '#6f5c50', fontSize: '0.68rem', fontWeight: 800 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: '#78965f', boxShadow: '0 0 0 3px rgba(120,150,95,0.15)' }} /> Online
            </Box>
            <LanguageSelect />
            <Button onClick={() => navigate('/vatuvu')} endIcon={<ArrowForwardIcon sx={{ fontSize: '15px !important' }} />} sx={{ display: { xs: 'none', sm: 'inline-flex' }, borderRadius: 99, px: 1.5, py: 0.85, color: '#fff', background: 'linear-gradient(110deg, #d9672b, #c88a2d)', textTransform: 'none', fontWeight: 800, fontSize: '0.72rem', boxShadow: '0 5px 12px rgba(201,112,43,0.2)' }}>Register</Button>
            {user ? (
              <>
                <IconButton onClick={event => setProfileAnchor(event.currentTarget)} sx={{ p: 0.2 }}><Avatar alt={user.firstName || user.name} src={user.avatar} sx={{ width: 34, height: 34, border: '2px solid #e6b25b' }} /></IconButton>
                <Menu anchorEl={profileAnchor} open={Boolean(profileAnchor)} onClose={() => setProfileAnchor(null)}>
                  <MenuItem component={RouterLink} to="/edit-profile" onClick={() => setProfileAnchor(null)}>Edit Profile</MenuItem>
                  <MenuItem onClick={handleLogout} sx={{ color: '#c0392b' }}>Logout</MenuItem>
                </Menu>
              </>
            ) : <IconButton onClick={() => setLoginDialogOpen(true)} sx={{ color: '#d9672b', p: 0.5 }}><AccountCircleIcon /></IconButton>}
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer open={drawerOpen} onClose={closeDrawer} ModalProps={{ keepMounted: true }} sx={{ zIndex: theme => theme.zIndex.drawer + 2 }} PaperProps={{ sx: { width: { xs: 'min(84vw, 320px)', sm: 300 }, background: '#fffaf1' } }}>{drawerContent}</Drawer>

      <Box component="main" sx={{ p: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 11, md: 14 }, minHeight: '100vh', boxSizing: 'border-box', overflowX: 'hidden', background: 'linear-gradient(145deg, #fffdf8 0%, #fff5e8 50%, #f9e3cb 100%)' }}><Outlet /></Box>

      <Dialog open={pathasalaConfirmOpen} onClose={() => setPathasalaConfirmOpen(false)}>
        <DialogTitle>{copy.registerTitle}</DialogTitle>
        <DialogContent><DialogContentText>{copy.message}</DialogContentText></DialogContent>
        <DialogActions><Button onClick={() => setPathasalaConfirmOpen(false)}>{copy.exit}</Button><Button variant="contained" onClick={() => { setPathasalaConfirmOpen(false); navigate('/pathasalaMgmt'); }}>{copy.register}</Button></DialogActions>
      </Dialog>
      <LoginDialogMulti open={loginDialogOpen} onClose={() => setLoginDialogOpen(false)} />
      <ChangePasswordDialog open={changePasswordDialogOpen} onClose={success => { if (success) { setChangePasswordDialogOpen(false); window.location.reload(); } }} isRequired={user?.requirePasswordChange} />
    </Box>
  );
}
